import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { StreamingText, StreamingLoader } from "@/components/StreamingText";
import { 
  Clock, 
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Flag,
  Zap,
  Lock,
  LogIn,
  BookOpen,
  Infinity,
  Lightbulb,
  Sparkles,
  Save,
  DoorOpen,
  Shuffle
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Colorful brain images
const BRAIN_LOGO = "https://customer-assets.emergentagent.com/job_e95fcea1-1d05-419a-a729-ac97ff58a502/artifacts/1u3r91fj_logo%20no%20back_Image_Jan_4__2026_at_02_55_25_PM-removebg-preview.png";
const BRAIN_QUIZ = "https://static.prod-images.emergentagent.com/jobs/8e9d7372-4ec9-4003-8f1e-d03dc24a1b4c/images/c8189de064c0a987b2b301aff93af08dae03f473f98554da8e4c62d9a5cb5213.png";

export default function QuizPage() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isPremium, loading: authLoading, authChecked } = useAuth();
  
  const [quiz, setQuiz] = useState(null);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isPracticeMode, setIsPracticeMode] = useState(false);
  const [isStudyMode, setIsStudyMode] = useState(searchParams.get("study") === "true");
  const [elapsedTime, setElapsedTime] = useState(0); // Track time in study mode
  
  // Review Mode states (for Study Mode)
  const [showReviewFeedback, setShowReviewFeedback] = useState(false);
  const [reviewExplanation, setReviewExplanation] = useState(null);
  const [loadingExplanation, setLoadingExplanation] = useState(false);
  const [reviewedQuestions, setReviewedQuestions] = useState(new Set());
  
  // AbortController ref to cancel pending AI explanation requests
  const abortControllerRef = useRef(null);
  // Track which question the current explanation request is for
  const currentExplanationQuestionRef = useRef(null);

  // Cancel any pending AI explanation request
  const cancelPendingExplanation = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    currentExplanationQuestionRef.current = null;
    setLoadingExplanation(false);
    setReviewExplanation(null);
    setShowReviewFeedback(false);
  }, []);

  // Cleanup AbortController on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Clear explanation state when question changes - safety net
  useEffect(() => {
    // Abort any pending request when question index changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    currentExplanationQuestionRef.current = null;
    setShowReviewFeedback(false);
    setReviewExplanation(null);
    setLoadingExplanation(false);
  }, [currentQuestionIndex]);

  // Fisher-Yates shuffle algorithm for randomizing questions
  const shuffleArray = useCallback((array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  useEffect(() => {
    // Check if we have a practice quiz passed via location state
    if (location.state?.practiceQuiz) {
      const practiceQuiz = location.state.practiceQuiz;
      setQuiz(practiceQuiz);
      // Shuffle questions for practice mode
      setShuffledQuestions(shuffleArray(practiceQuiz.questions));
      setTimeRemaining(practiceQuiz.time_limit * 60);
      setIsPracticeMode(true);
      setLoading(false);
      return;
    }
    
    // Check if this is a practice exam ID
    if (quizId && quizId.startsWith("practice-exam-")) {
      // Check sessionStorage for practice exam
      const storedExam = sessionStorage.getItem("practiceExam");
      if (storedExam) {
        try {
          const practiceExam = JSON.parse(storedExam);
          if (practiceExam.id === quizId) {
            setQuiz(practiceExam);
            // Questions are already shuffled by the generator
            setShuffledQuestions(practiceExam.questions);
            setTimeRemaining(practiceExam.time_limit * 60);
            setIsPracticeMode(true);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error("Failed to parse practice exam:", e);
        }
      }
      // If no valid practice exam found, redirect to generator
      navigate("/practice-exam");
      return;
    }
    
    // Regular quiz - fetch from API
    fetchQuiz();
  }, [quizId, location.state, navigate, shuffleArray]);

  const fetchQuiz = async () => {
    try {
      const response = await axios.get(`${API}/quizzes/${quizId}`);
      setQuiz(response.data);
      // Shuffle questions using Fisher-Yates algorithm for varied learning
      setShuffledQuestions(shuffleArray(response.data.questions));
      setTimeRemaining(response.data.time_limit * 60);
    } catch (error) {
      toast.error("Failed to load quiz");
      navigate("/quizzes");
    } finally {
      setLoading(false);
    }
  };

  const submitQuiz = useCallback(async () => {
    if (quizFinished) return;
    setQuizFinished(true);
    
    const results = quiz.questions.map(q => {
      // Handle multi-select questions
      if (q.question_type === "select_all") {
        const selectedAnswers = answers[q.id] || [];
        const correctAnswers = q.correct_answers || [];
        const isCorrect = correctAnswers.length === selectedAnswers.length && 
          correctAnswers.every(ans => selectedAnswers.includes(ans));
        return {
          question_id: q.id,
          question_text: q.text,
          selected_answer: selectedAnswers,
          correct_answer: correctAnswers,
          is_correct: isCorrect,
          explanation: q.explanation,
          all_options: q.options,
          question_type: "select_all"
        };
      }
      
      // Standard single-answer questions
      return {
        question_id: q.id,
        question_text: q.text,
        selected_answer: answers[q.id] || null,
        correct_answer: q.correct_answer,
        is_correct: answers[q.id] === q.correct_answer,
        explanation: q.explanation,
        all_options: q.options
      };
    });

    const score = results.filter(r => r.is_correct).length;
    const percentage = Math.round((score / quiz.questions.length) * 100);
    // In study mode, use elapsed time; in timed mode, calculate from remaining time
    const timeTaken = isStudyMode ? elapsedTime : (quiz.time_limit * 60) - timeRemaining;

    const attemptData = {
      quiz_id: quiz.id,
      quiz_title: quiz.title,
      category: quiz.category,
      score,
      total_questions: quiz.questions.length,
      percentage,
      time_taken: timeTaken,
      answers: results,
      study_mode: isStudyMode  // Track if this was study mode
    };

    // Show loading toast while saving
    const loadingToast = toast.loading("Saving your results...");
    
    try {
      await axios.post(`${API}/attempts`, attemptData);
      toast.dismiss(loadingToast);
      toast.success(isStudyMode ? "Study session completed!" : "Quiz completed!");
      // Small delay for smoother transition
      setTimeout(() => {
        navigate("/results", { state: { results: attemptData } });
      }, 100);
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error("Failed to save results, showing anyway");
      setTimeout(() => {
        navigate("/results", { state: { results: attemptData } });
      }, 100);
    }
  }, [quiz, answers, timeRemaining, elapsedTime, isStudyMode, quizFinished, navigate]);

  // Timer - only run countdown in timed mode, count up in study mode
  useEffect(() => {
    if (!quizStarted || quizFinished) return;

    // Study mode: count up (no time limit)
    if (isStudyMode) {
      const timer = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    }

    // Timed mode: count down
    if (timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          submitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [quizStarted, quizFinished, isStudyMode, submitQuiz, timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    const totalTime = quiz?.time_limit * 60 || 0;
    const percentRemaining = (timeRemaining / totalTime) * 100;
    if (percentRemaining <= 10) return "text-red-500";
    if (percentRemaining <= 25) return "text-amber-500";
    return "text-slate-700 dark:text-slate-300";
  };

  const handleAnswerSelect = async (answer) => {
    if (quizFinished) return;
    
    // Handle multi-select questions (select_all type)
    if (currentQuestion.question_type === "select_all") {
      setAnswers(prev => {
        const currentSelections = prev[currentQuestion.id] || [];
        const isAlreadySelected = currentSelections.includes(answer);
        
        if (isAlreadySelected) {
          // Remove the answer
          return {
            ...prev,
            [currentQuestion.id]: currentSelections.filter(a => a !== answer)
          };
        } else {
          // Add the answer
          return {
            ...prev,
            [currentQuestion.id]: [...currentSelections, answer]
          };
        }
      });
      return; // Don't trigger review mode on each selection for multi-select
    }
    
    // Store the answer for single-select questions
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: answer
    }));
    
    // In Study Mode, show immediate feedback with AI explanation
    if (isStudyMode && !reviewedQuestions.has(currentQuestion.id)) {
      const isCorrect = answer === currentQuestion.correct_answer;
      const questionId = currentQuestion.id; // Capture question ID for this request
      
      // Cancel any previous pending request first
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Set up state for this question
      setShowReviewFeedback(true);
      setLoadingExplanation(true);
      setReviewedQuestions(prev => new Set([...prev, questionId]));
      
      // Track which question this explanation is for
      currentExplanationQuestionRef.current = questionId;
      
      // Initialize explanation state
      setReviewExplanation({
        isCorrect,
        selectedAnswer: answer,
        correctAnswer: currentQuestion.correct_answer,
        explanation: "",
        basicExplanation: currentQuestion.explanation,
        questionId: questionId // Track the question ID in the state
      });
      
      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      
      // Fetch AI explanation with streaming
      try {
        const response = await fetch(`${API}/explain-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            question: currentQuestion.text,
            selected_answer: answer,
            correct_answer: currentQuestion.correct_answer,
            is_correct: isCorrect,
            all_options: currentQuestion.options
          }),
          signal: abortController.signal
        });
        
        // Check if aborted before processing response
        if (abortController.signal.aborted || currentExplanationQuestionRef.current !== questionId) {
          return;
        }
        
        if (!response.ok) {
          throw new Error('Failed to fetch explanation');
        }
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let streamedText = "";
        
        try {
          // Stream the response
          while (true) {
            // Check if aborted or question changed before each read
            if (abortController.signal.aborted || currentExplanationQuestionRef.current !== questionId) {
              await reader.cancel();
              return; // Exit completely
            }
            
            const { done, value } = await reader.read();
            if (done) break;
            
            // Double-check after async read
            if (abortController.signal.aborted || currentExplanationQuestionRef.current !== questionId) {
              await reader.cancel();
              return; // Exit completely
            }
            
            const chunk = decoder.decode(value, { stream: true });
            streamedText += chunk;
            
            // Only update if this is still the current question
            setReviewExplanation(prev => {
              // Guard: don't update if question changed
              if (!prev || prev.questionId !== questionId) return prev;
              if (currentExplanationQuestionRef.current !== questionId) return prev;
              return { ...prev, explanation: streamedText };
            });
          }
        } finally {
          // Always try to cancel reader when done
          try { await reader.cancel(); } catch (e) { /* ignore */ }
        }
        
        // Only update loading state if still on same question
        if (currentExplanationQuestionRef.current === questionId && !abortController.signal.aborted) {
          setLoadingExplanation(false);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        // Only show fallback if still on same question
        if (currentExplanationQuestionRef.current === questionId) {
          setReviewExplanation({
            isCorrect,
            selectedAnswer: answer,
            correctAnswer: currentQuestion.correct_answer,
            explanation: null,
            basicExplanation: currentQuestion.explanation || "Review the correct answer and study this concept further.",
            questionId: questionId
          });
          setLoadingExplanation(false);
        }
      }
    }
  };

  // Helper function to check if multi-select answer is correct
  const isMultiSelectCorrect = (selectedAnswers, correctAnswers) => {
    if (!selectedAnswers || !correctAnswers) return false;
    if (selectedAnswers.length !== correctAnswers.length) return false;
    return correctAnswers.every(ans => selectedAnswers.includes(ans));
  };

  // Submit multi-select answer for review in study mode
  const submitMultiSelectAnswer = async () => {
    if (!currentQuestion || currentQuestion.question_type !== "select_all") return;
    if (reviewedQuestions.has(currentQuestion.id)) return;
    
    const selectedAnswers = answers[currentQuestion.id] || [];
    const correctAnswers = currentQuestion.correct_answers || [];
    const isCorrect = isMultiSelectCorrect(selectedAnswers, correctAnswers);
    const questionId = currentQuestion.id; // Capture question ID
    
    // Cancel any previous pending request first
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setShowReviewFeedback(true);
    setLoadingExplanation(true);
    setReviewedQuestions(prev => new Set([...prev, questionId]));
    
    // Track which question this explanation is for
    currentExplanationQuestionRef.current = questionId;
    
    setReviewExplanation({
      isCorrect,
      selectedAnswer: selectedAnswers.join(", "),
      correctAnswer: correctAnswers.join(", "),
      explanation: "",
      basicExplanation: currentQuestion.explanation,
      isMultiSelect: true,
      selectedAnswers,
      correctAnswers,
      questionId: questionId
    });
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      const response = await fetch(`${API}/explain-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: currentQuestion.text,
          selected_answer: selectedAnswers.join(", "),
          correct_answer: correctAnswers.join(", "),
          is_correct: isCorrect,
          all_options: currentQuestion.options
        }),
        signal: abortController.signal
      });
      
      // Check if aborted before processing response
      if (abortController.signal.aborted || currentExplanationQuestionRef.current !== questionId) {
        return;
      }
      
      if (!response.ok) throw new Error('Failed to fetch explanation');
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedText = "";
      
      try {
        while (true) {
          if (abortController.signal.aborted || currentExplanationQuestionRef.current !== questionId) {
            await reader.cancel();
            return;
          }
          
          const { done, value } = await reader.read();
          if (done) break;
          
          // Double-check after async read
          if (abortController.signal.aborted || currentExplanationQuestionRef.current !== questionId) {
            await reader.cancel();
            return;
          }
          
          const chunk = decoder.decode(value, { stream: true });
          streamedText += chunk;
          
          setReviewExplanation(prev => {
            if (!prev || prev.questionId !== questionId) return prev;
            if (currentExplanationQuestionRef.current !== questionId) return prev;
            return { ...prev, explanation: streamedText };
          });
        }
      } finally {
        try { await reader.cancel(); } catch (e) { /* ignore */ }
      }
      
      if (currentExplanationQuestionRef.current === questionId && !abortController.signal.aborted) {
        setLoadingExplanation(false);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      if (currentExplanationQuestionRef.current === questionId) {
        setReviewExplanation(prev => ({
          ...prev,
          explanation: null,
          basicExplanation: currentQuestion.explanation || "Review the correct answers and study this concept further."
        }));
        setLoadingExplanation(false);
      }
    }
  };

  const continueToNextQuestion = () => {
    cancelPendingExplanation();
    setShowReviewFeedback(false);
    setReviewExplanation(null);
    if (currentQuestionIndex < shuffledQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const goToNextQuestion = () => {
    // In Study Mode with review, don't auto-advance if showing feedback
    if (isStudyMode && showReviewFeedback) return;
    
    cancelPendingExplanation();
    if (currentQuestionIndex < shuffledQuestions.length - 1) {
      setShowReviewFeedback(false);
      setReviewExplanation(null);
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const goToPrevQuestion = () => {
    cancelPendingExplanation();
    if (currentQuestionIndex > 0) {
      setShowReviewFeedback(false);
      setReviewExplanation(null);
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  // Re-shuffle questions mid-session
  const reshuffleQuestions = () => {
    cancelPendingExplanation();
    const newShuffled = shuffleArray(quiz.questions);
    setShuffledQuestions(newShuffled);
    setCurrentQuestionIndex(0);
    setShowReviewFeedback(false);
    setReviewExplanation(null);
    toast.success("Questions reshuffled! Starting from question 1");
  };

  // Show login required screen if not authenticated or not premium
  if (authChecked && !authLoading && (!isAuthenticated || !isPremium)) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50/30 via-white to-teal-50/20 flex items-center justify-center p-6">
        <motion.div 
          className="max-w-md w-full text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-amber-100 p-8 shadow-lg">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-teal-100 flex items-center justify-center">
              <Lock className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              {isAuthenticated ? "Subscription Required" : "Login Required"}
            </h1>
            <p className="text-slate-600 mb-6">
              {isAuthenticated 
                ? "Subscribe to access this quiz and save your progress toward BCBA certification."
                : "Sign in and subscribe to access this quiz and save your progress toward BCBA certification."
              }
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate("/auth")}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
                data-testid="login-btn"
              >
                <LogIn className="w-5 h-5 mr-2" />
                {isAuthenticated ? "Subscribe Now - $7.00/month" : "Sign In / Create Account"}
              </Button>
              <Button 
                variant="ghost"
                onClick={() => navigate("/")}
                className="w-full text-slate-600"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!quiz || shuffledQuestions.length === 0) return null;

  const currentQuestion = shuffledQuestions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / shuffledQuestions.length) * 100;
  const answeredCount = Object.keys(answers).length;

  // Start screen
  if (!quizStarted) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-6">
        <motion.div 
          className="max-w-lg w-full bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-xl border border-slate-200 dark:border-slate-700"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="text-center">
            {isPracticeMode && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm font-medium mb-4">
                <Zap className="w-4 h-4" />
                Weak Areas Practice
              </div>
            )}
            <div className={`w-20 h-20 rounded-2xl ${isPracticeMode ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-transparent'} flex items-center justify-center mx-auto mb-6 overflow-hidden`}>
              {isPracticeMode ? (
                <Zap className="w-10 h-10 text-amber-600" />
              ) : (
                <motion.img 
                  src={BRAIN_QUIZ} 
                  alt="Brain" 
                  className="w-20 h-20 object-cover drop-shadow-lg"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              )}
            </div>
            <h1 className="text-2xl font-bold mb-2 text-slate-800 dark:text-slate-100" data-testid="quiz-title">{quiz.title}</h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{quiz.description}</p>
            
            {isPracticeMode && (
              <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl text-left border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  <strong>Targeted Practice:</strong> These questions are based on areas where you&apos;ve struggled. 
                  Focus on understanding the concepts behind each question.
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
                <p className={`text-2xl font-bold ${isPracticeMode ? 'text-amber-600' : 'text-teal-600'}`}>{shuffledQuestions.length}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Questions</p>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
                <p className={`text-2xl font-bold ${isPracticeMode ? 'text-amber-600' : isStudyMode ? 'text-violet-600' : 'text-teal-600'}`}>
                  {isStudyMode ? '∞' : quiz.time_limit}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">{isStudyMode ? 'No Limit' : 'Minutes'}</p>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
                <p className={`text-2xl font-bold ${isPracticeMode ? 'text-amber-600' : 'text-teal-600'} capitalize`}>{quiz.difficulty}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Difficulty</p>
              </div>
            </div>

            {/* Study Mode Toggle - Premium Feature */}
            {isPremium && !isPracticeMode && (
              <div className="mb-6 p-4 bg-violet-50 dark:bg-violet-900/20 rounded-xl border border-violet-200 dark:border-violet-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-800 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-violet-600 dark:text-violet-300" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-slate-800 dark:text-slate-100">Study Mode</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">No time limit - learn at your pace</p>
                    </div>
                  </div>
                  <Switch
                    checked={isStudyMode}
                    onCheckedChange={setIsStudyMode}
                    data-testid="study-mode-toggle"
                  />
                </div>
                {isStudyMode && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-3 pt-3 border-t border-violet-200 dark:border-violet-700"
                  >
                    <p className="text-sm text-violet-700 dark:text-violet-300 flex items-center gap-2">
                      <Infinity className="w-4 h-4" />
                      Take your time to review each question thoroughly
                    </p>
                  </motion.div>
                )}
              </div>
            )}

            {/* Shuffled Questions Indicator */}
            <div className="mb-4 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-800">
              <p className="text-sm text-teal-700 dark:text-teal-300 flex items-center gap-2">
                <Shuffle className="w-4 h-4" />
                Questions appear in random order for varied learning
              </p>
            </div>

            <div className="space-y-3">
              <Button 
                className={`w-full py-6 text-lg ${
                  isStudyMode 
                    ? 'bg-violet-500 hover:bg-violet-600' 
                    : isPracticeMode 
                    ? 'bg-amber-500 hover:bg-amber-600' 
                    : 'bg-teal-500 hover:bg-teal-600'
                } text-white`}
                onClick={() => setQuizStarted(true)}
                data-testid="start-quiz-btn"
              >
                {isStudyMode ? (
                  <>
                    <BookOpen className="w-5 h-5 mr-2" />
                    Start Study Session
                  </>
                ) : isPracticeMode ? (
                  'Start Practice'
                ) : (
                  'Start Quiz'
                )}
              </Button>
              <Button 
                variant="outline" 
                className="w-full border-slate-300 text-slate-700 dark:border-slate-600 dark:text-slate-300"
                onClick={() => isPracticeMode ? navigate("/analytics") : navigate("/quizzes")}
                data-testid="back-to-quizzes-btn"
              >
                {isPracticeMode ? 'Back to Analytics' : 'Back to Quizzes'}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/30 via-white to-teal-50/20 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900">
      {/* Header */}
      <header className="bg-white/80 dark:bg-slate-800/90 backdrop-blur-sm border-b border-amber-100 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl overflow-hidden">
                <img src={BRAIN_LOGO} alt="Brain Logo" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-bold text-slate-900 dark:text-slate-100" data-testid="quiz-header-title">{quiz.title}</h1>
                  {isStudyMode && (
                    <Badge className="bg-violet-100 text-violet-700 border-violet-200">
                      <BookOpen className="w-3 h-3 mr-1" />
                      Study Mode
                    </Badge>
                  )}
                </div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{quiz.category}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Re-shuffle Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={reshuffleQuestions}
                className="text-teal-600 border-teal-200 hover:bg-teal-50 hover:border-teal-300"
                data-testid="reshuffle-btn"
                title="Shuffle questions into a new random order"
              >
                <Shuffle className="w-4 h-4 mr-1" />
                Re-shuffle
              </Button>
              
              {isStudyMode ? (
                <div className="flex items-center gap-2 text-lg font-mono font-semibold text-violet-600">
                  <Infinity className="w-5 h-5" />
                  <span data-testid="timer">{formatTime(elapsedTime)}</span>
                </div>
              ) : (
                <div className={`flex items-center gap-2 text-lg font-mono font-semibold ${getTimerColor()}`}>
                  <Clock className="w-5 h-5" />
                  <span data-testid="timer">{formatTime(timeRemaining)}</span>
                </div>
              )}
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              <span>Question {currentQuestionIndex + 1} of {shuffledQuestions.length}</span>
              <span>{answeredCount} answered</span>
            </div>
            <Progress value={progress} className="h-2" data-testid="progress-bar" />
          </div>
        </div>
      </header>

      {/* Question */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-2xl border border-amber-100 dark:border-slate-700 p-8 mb-6 shadow-sm">
              <div className="flex items-start gap-3 mb-6">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-sm font-bold text-teal-700">
                  {currentQuestionIndex + 1}
                </span>
                <div>
                  <span className="text-xs text-teal-600 uppercase tracking-wider font-semibold mb-2 block">
                    {currentQuestion.question_type === "true_false" 
                      ? "True or False" 
                      : currentQuestion.question_type === "select_all"
                      ? "Select All That Apply"
                      : "Multiple Choice"}
                  </span>
                  {currentQuestion.question_type === "select_all" && (
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium mb-3">
                      <CheckCircle2 className="w-3 h-3" />
                      Choose all that apply
                    </div>
                  )}
                  <h2 className="text-xl font-semibold leading-relaxed text-slate-900 dark:text-slate-100" data-testid="question-text">
                    {currentQuestion.text}
                  </h2>
                </div>
              </div>

              <div className="space-y-3">
                {currentQuestion.options.map((option, index) => {
                  const isMultiSelect = currentQuestion.question_type === "select_all";
                  const currentSelections = isMultiSelect ? (answers[currentQuestion.id] || []) : null;
                  const isSelected = isMultiSelect 
                    ? currentSelections.includes(option)
                    : answers[currentQuestion.id] === option;
                  const correctAnswers = isMultiSelect ? (currentQuestion.correct_answers || []) : [currentQuestion.correct_answer];
                  const isCorrectAnswer = correctAnswers.includes(option);
                  const showReviewHighlight = isStudyMode && showReviewFeedback && reviewedQuestions.has(currentQuestion.id);
                  
                  let optionStyle = '';
                  if (showReviewHighlight) {
                    if (isCorrectAnswer) {
                      optionStyle = 'border-green-500 bg-green-50 dark:bg-green-900/20';
                    } else if (isSelected && !isCorrectAnswer) {
                      optionStyle = 'border-red-500 bg-red-50 dark:bg-red-900/20';
                    }
                  }
                  
                  return (
                    <motion.button
                      key={index}
                      whileHover={{ scale: showReviewHighlight ? 1 : 1.01 }}
                      whileTap={{ scale: showReviewHighlight ? 1 : 0.99 }}
                      onClick={() => !showReviewHighlight && handleAnswerSelect(option)}
                      disabled={showReviewHighlight}
                      className={`quiz-option w-full text-left flex items-center gap-4 ${isSelected && !showReviewHighlight ? 'selected' : ''} ${optionStyle} ${showReviewHighlight ? 'cursor-default' : ''}`}
                      data-testid={`option-${index}`}
                    >
                      {/* Checkbox (square) for multi-select, Circle for single select */}
                      <span className={`w-9 h-9 ${isMultiSelect ? 'rounded-md' : 'rounded-full'} border-2 flex items-center justify-center text-sm font-bold transition-colors ${
                        showReviewHighlight && isCorrectAnswer
                          ? 'border-green-500 bg-green-500 text-white'
                          : showReviewHighlight && isSelected && !isCorrectAnswer
                          ? 'border-red-500 bg-red-500 text-white'
                          : isSelected 
                          ? isMultiSelect ? 'border-purple-500 bg-purple-500 text-white' : 'border-teal-500 bg-teal-500 text-white' 
                          : 'border-gray-500 text-gray-700'
                      }`}>
                        {showReviewHighlight && isCorrectAnswer ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : showReviewHighlight && isSelected && !isCorrectAnswer ? (
                          <XCircle className="w-5 h-5" />
                        ) : isMultiSelect && isSelected ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          String.fromCharCode(65 + index)
                        )}
                      </span>
                      <span className="flex-1 font-medium text-base" style={{ color: '#000000' }}>{option}</span>
                      {!showReviewHighlight && isSelected && <CheckCircle2 className={`w-5 h-5 ${isMultiSelect ? 'text-purple-500' : 'text-teal-500'}`} />}
                    </motion.button>
                  );
                })}
              </div>

              {/* Submit button for multi-select questions in Study Mode */}
              {isStudyMode && currentQuestion.question_type === "select_all" && !reviewedQuestions.has(currentQuestion.id) && (
                <div className="mt-6 flex justify-center">
                  <Button
                    onClick={submitMultiSelectAnswer}
                    disabled={(answers[currentQuestion.id] || []).length === 0}
                    className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-3 font-semibold"
                    data-testid="submit-multi-select-btn"
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Submit Answer ({(answers[currentQuestion.id] || []).length} selected)
                  </Button>
                </div>
              )}

              {/* Review Mode Feedback Panel */}
              <AnimatePresence>
                {isStudyMode && showReviewFeedback && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="mt-6"
                  >
                    {loadingExplanation && !reviewExplanation?.explanation ? (
                      <div className="bg-violet-50 dark:bg-violet-900/20 rounded-xl p-6 border border-violet-200 dark:border-violet-800">
                        <StreamingLoader message="Getting AI explanation..." />
                        {/* Skip button while loading */}
                        <div className="mt-4 pt-4 border-t border-violet-200 dark:border-violet-700">
                          <Button
                            onClick={continueToNextQuestion}
                            variant="outline"
                            className="w-full py-4 border-violet-300 text-violet-700 hover:bg-violet-100 dark:border-violet-600 dark:text-violet-300 dark:hover:bg-violet-800"
                            data-testid="skip-explanation-btn"
                          >
                            Skip Explanation
                            <ChevronRight className="w-5 h-5 ml-2" />
                          </Button>
                        </div>
                      </div>
                    ) : reviewExplanation && (
                      <div className={`rounded-xl p-6 border ${
                        reviewExplanation.isCorrect 
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      }`}>
                        {/* Correct/Incorrect Banner */}
                        <div className={`flex items-center gap-3 mb-4 pb-4 border-b ${
                          reviewExplanation.isCorrect ? 'border-green-200 dark:border-green-700' : 'border-red-200 dark:border-red-700'
                        }`}>
                          {reviewExplanation.isCorrect ? (
                            <>
                              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                                <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-300" />
                              </div>
                              <div>
                                <p className="font-bold text-green-700 dark:text-green-300 text-lg">Correct!</p>
                                <p className="text-sm text-green-600 dark:text-green-400">Great job! Keep up the good work.</p>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-800 flex items-center justify-center">
                                <XCircle className="w-6 h-6 text-red-600 dark:text-red-300" />
                              </div>
                              <div>
                                <p className="font-bold text-red-700 dark:text-red-300 text-lg">Incorrect</p>
                                <p className="text-sm text-red-600 dark:text-red-400">
                                  The correct answer is: <strong>{reviewExplanation.correctAnswer}</strong>
                                </p>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Explanation with Streaming Animation */}
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <Lightbulb className={`w-5 h-5 mt-1 flex-shrink-0 ${
                              reviewExplanation.isCorrect ? 'text-green-600' : 'text-red-600'
                            }`} />
                            <div className="flex-1">
                              <p className="font-semibold text-slate-800 dark:text-slate-200 mb-2">Explanation</p>
                              {reviewExplanation.explanation ? (
                                <StreamingText 
                                  text={reviewExplanation.explanation}
                                  isStreaming={loadingExplanation}
                                  className="text-slate-700 dark:text-slate-300"
                                />
                              ) : (
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                  {reviewExplanation.basicExplanation}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Continue Button - Always visible, floating at bottom */}
                        <div className="sticky bottom-0 mt-6 pt-4 pb-2 bg-gradient-to-t from-white via-white to-transparent dark:from-slate-900 dark:via-slate-900">
                          <Button
                            onClick={continueToNextQuestion}
                            className={`w-full py-6 ${
                              reviewExplanation.isCorrect 
                                ? 'bg-green-600 hover:bg-green-700' 
                                : 'bg-violet-600 hover:bg-violet-700'
                            } text-white font-semibold shadow-lg`}
                            data-testid="continue-btn"
                          >
                            {loadingExplanation ? (
                              <>
                                Skip Explanation
                                <ChevronRight className="w-5 h-5 ml-2" />
                              </>
                            ) : currentQuestionIndex < shuffledQuestions.length - 1 ? (
                              <>
                                Continue to Next Question
                                <ChevronRight className="w-5 h-5 ml-2" />
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-5 h-5 mr-2" />
                                Finish Study Session
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={goToPrevQuestion}
                disabled={currentQuestionIndex === 0}
                data-testid="prev-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>

              <div className="flex gap-2">
                {shuffledQuestions.map((q, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentQuestionIndex(index)}
                    className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                      index === currentQuestionIndex
                        ? 'bg-violet-600 text-white'
                        : answers[q.id]
                        ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-600'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                    }`}
                    data-testid={`question-nav-${index}`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              {currentQuestionIndex === shuffledQuestions.length - 1 ? (
                <Button
                  onClick={submitQuiz}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="submit-btn"
                >
                  <Flag className="w-4 h-4 mr-2" />
                  Finish Quiz
                </Button>
              ) : (
                <Button
                  onClick={goToNextQuestion}
                  data-testid="next-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>

            {/* Save Progress and Exit Buttons */}
            <div className="flex justify-center gap-4 mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
              <Button
                variant="outline"
                onClick={() => {
                  toast.success("Progress saved");
                  navigate("/quizzes");
                }}
                className="text-slate-600 hover:text-teal-600"
                data-testid="save-progress-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Progress
              </Button>
              
              {/* Next button - centered between Save and Exit (not on last question) */}
              {currentQuestionIndex < shuffledQuestions.length - 1 && (
                <Button
                  onClick={goToNextQuestion}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8"
                  data-testid="bottom-next-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              
              {/* Finish Quiz button - centered between Save and Exit (on last question) */}
              {currentQuestionIndex === shuffledQuestions.length - 1 && (
                <Button
                  onClick={submitQuiz}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                  data-testid="bottom-finish-btn"
                >
                  <Flag className="w-4 h-4 mr-2" />
                  Finish Quiz
                </Button>
              )}
              
              <Button
                variant="ghost"
                onClick={() => navigate("/quizzes")}
                className="text-slate-500 hover:text-slate-700"
                data-testid="exit-btn"
              >
                <DoorOpen className="w-4 h-4 mr-2" />
                Exit
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
